import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Bot } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'

type Props = {
  open: boolean
  isGenerating: boolean
  isDiscarding: boolean
  isCommitting: boolean
  onBackToConfig: () => void
  onRegenerate: () => void
  onDiscard: () => void
  onCommit: () => void
}

const AutoSetupPreviewBar = ({
  open,
  isGenerating,
  isDiscarding,
  isCommitting,
  onBackToConfig,
  onRegenerate,
  onDiscard,
  onCommit,
}: Props) => {
  const reduceMotion = useReducedMotion()
  const busy = isGenerating || isDiscarding || isCommitting

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className='fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 pointer-events-none'
          data-auto-setup-preview-bar
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 80 }}
          transition={
            reduceMotion
              ? { duration: 0.15 }
              : { type: 'spring', stiffness: 460, damping: 22 }
          }
        >
          <Card size='sm' className='pointer-events-auto shadow-lg'>
            <div className='flex flex-row flex-wrap items-center gap-3'>
              <Bot className='h-5 w-5 shrink-0 text-info-foreground' />
              <div className='min-w-32'>
                <div className='text-sm font-medium leading-tight'>
                  Previewing generated setup
                </div>
                <div className='text-xs text-muted-foreground leading-tight'>
                  Manual changes are kept until you commit
                </div>
              </div>
              <Button
                variant='outline'
                onClick={onBackToConfig}
                disabled={busy}
              >
                Config
              </Button>
              <Button
                variant='outline'
                loading={isGenerating}
                onClick={onRegenerate}
                disabled={isCommitting || isDiscarding}
              >
                Regenerate
              </Button>
              <Button
                variant='outline'
                loading={isDiscarding}
                onClick={onDiscard}
                disabled={isGenerating || isCommitting}
              >
                Discard
              </Button>
              <Button
                loading={isCommitting}
                onClick={onCommit}
                disabled={isGenerating || isDiscarding}
              >
                Commit
              </Button>
            </div>
          </Card>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

export default AutoSetupPreviewBar
